import apiClient from './client';

/**
 * MDF-127 — extra email addresses that let a Paperform submission reach a
 * participant whose form used an address we do not hold.
 *
 * Goldman Sachs uses four email domains across 274 users, so one person has
 * several valid work addresses and a submission sent from the wrong one finds
 * nobody.
 *
 * An alias never signs anybody in. It is read by the Paperform matching paths
 * only, and the API has a test that holds that line.
 */

export interface EmailAlias {
  id: string;
  user_id: string;
  email: string;
  created_by: string | null;
  /** ISO 8601. The API types this as a Date; JSON makes it a string. */
  created_at: string;
  /**
   * True when a live user now holds this address as their own sign in
   * address. A primary address always beats an alias, so a shadowed alias
   * matches nothing and the editor has to say so.
   */
  shadowed: boolean;
}

/**
 * What adding the alias recovered. Adding an alias replays the submissions
 * the address already dropped, so these counts describe real writes.
 */
export interface EmailAliasRecovery {
  /** Stored webhook events that now link to a participant. */
  replayed_webhook_events: number;
  /** Events whose submission was already stored, now linked to it. */
  relinked_webhook_events: number;
  /** True when more stored orphans carry this address than one run replays. */
  replay_capped: boolean;
  /**
   * Submissions re-imported from Paperform for this participant.
   *
   * The re-import is scoped to this one person, so it never writes a row
   * against anybody else in their cohorts.
   */
  backfilled_submissions: number;
  cohorts_checked: number;
  failures: EmailAliasRecoveryFailure[];
}

/**
 * Which part of the recovery did not run.
 *
 * The four cases need four different sentences, and the screen cannot tell
 * them apart from a free text reason. Reporting them as one list of "cohorts
 * that could not be checked" named a cohort for a failure that touches no
 * cohort, and told an admin to try again when trying again can never succeed.
 *
 *   recovery_failed  Nothing ran. Every count is zero and means nothing.
 *   replay_failed    Stored responses were not replayed. No cohort involved.
 *   cohort_failed    One cohort's re-import from Paperform failed.
 *   cohort_over_cap  More cohorts than one pass checks. The same cohorts are
 *                    skipped every time, so saving again never reaches them.
 *   replay_held      Another run holds some stored responses right now. Saving
 *                    again in a few minutes reaches them.
 */
export type EmailAliasRecoveryFailureKind =
  | 'recovery_failed'
  | 'replay_failed'
  | 'cohort_failed'
  | 'cohort_over_cap'
  | 'replay_held';

export interface EmailAliasRecoveryFailure {
  kind: EmailAliasRecoveryFailureKind;
  /** The cohort, when the failure belongs to one. Null otherwise. */
  cohortId: string | null;
  reason: string;
  /** True when saving the address again can reach what this failure missed. */
  retryable: boolean;
}

export interface AddEmailAliasResult {
  alias: EmailAlias;
  recovery: EmailAliasRecovery;
}

/**
 * Adding an alias replays the responses the address already dropped, inside
 * the request. That is up to 50 stored events plus one Paperform fetch for
 * each of up to five cohorts, and a matching form can hold hundreds of
 * responses. The apiClient default of 30 seconds is far too tight, and
 * MED-1019 already hit this bug class: valid AI composes timed out and looked
 * like a hang.
 *
 * Why 110 seconds and not 120. The production load balancer closes an idle
 * connection at 120 seconds: `idle_timeout.timeout_seconds=120` in
 * medley-infrastructure/gitops/medley/main/production-medley-api.yaml. A
 * client timeout of 120 seconds is the same number, so which one fires first
 * is decided by network latency.
 *
 * That matters because the two failures do not look alike. A client timeout
 * arrives with no HTTP status, and the panel knows to re-read the list and
 * check whether the address was saved. A load balancer timeout arrives as a
 * real HTTP 504, which is easy to mistake for the API refusing the request.
 *
 * Ten seconds under the load balancer makes the client win that race every
 * time, so the failure is always the one the panel handles best. The panel
 * still treats 502, 503 and 504 as "no answer" for the same reason, because a
 * pod restart can produce one at any moment.
 *
 * The timeout still guarantees the promise settles, so the caller's loading
 * state always resolves. It never hangs for ever.
 */
const ADD_ALIAS_TIMEOUT_MS = 110_000;

export const emailAliasesApi = {
  list: async (
    cohortId: string,
    participantId: string
  ): Promise<EmailAlias[]> => {
    const result = await apiClient.get<{ email_aliases: EmailAlias[] }>(
      `/api/cohorts/${cohortId}/participants/${participantId}/email-aliases`
    );
    return result.email_aliases;
  },

  add: async (
    cohortId: string,
    participantId: string,
    email: string
  ): Promise<AddEmailAliasResult> => {
    return apiClient.post<AddEmailAliasResult>(
      `/api/cohorts/${cohortId}/participants/${participantId}/email-aliases`,
      { email },
      { timeout: ADD_ALIAS_TIMEOUT_MS }
    );
  },

  remove: async (
    cohortId: string,
    participantId: string,
    aliasId: string
  ): Promise<void> => {
    await apiClient.delete<void>(
      `/api/cohorts/${cohortId}/participants/${participantId}/email-aliases/${aliasId}`
    );
  },
};

export default emailAliasesApi;
